import { useEffect, useRef, useState } from "react";
import { SlidersHorizontal } from "lucide-react";

import { EditResultModal } from "@/components/exams/exams-tab/exam-pivot-view";
import { useExamArbitrateResult } from "@/hooks/use-exams";
import { ExamLabels } from "@/lib/labels/exams";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { ExamsPivot } from "@/api/db/exams";

function ModelMenu({
	readings,
	disabled,
	onChoose,
}: {
	readings: { model: string; reading: string }[];
	disabled: boolean;
	onChoose: (model: string) => void;
}) {
	const rootRef = useRef<HTMLSpanElement>(null);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!open) {
			return;
		}

		const handlePointerDown = (event: PointerEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setOpen(false);
			}
		};

		document.addEventListener("pointerdown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("pointerdown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open]);

	return (
		<span ref={rootRef} className="relative inline-flex">
			<Button
				type="button"
				variant="secondary"
				size="sm"
				data-slot="model-menu-open"
				aria-expanded={open}
				disabled={disabled}
				className={cn(open && "bg-secondary text-foreground")}
				onClick={() => setOpen((value) => !value)}
			>
				<SlidersHorizontal />
				{ExamLabels.readingModel.choose}
			</Button>

			{open && (
				<div className="popover-panel absolute left-0 top-full z-20 mt-1.5 min-w-40 p-1">
					<p className="px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
						{ExamLabels.readingModel.header}
					</p>

					{readings.map((reading) => (
						<button
							key={reading.model}
							type="button"
							disabled={disabled}
							onClick={() => {
								onChoose(reading.model);
								setOpen(false);
							}}
							className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-secondary focus-visible:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
						>
							<span className="size-1.5 shrink-0 rounded-full bg-primary" />
							{reading.model}
						</button>
					))}
				</div>
			)}
		</span>
	);
}

function DivergentRow({ cell }: { cell: NonNullable<ExamsPivot["divergent"][number]> }) {
	const arbitrate = useExamArbitrateResult();
	const [editing, setEditing] = useState(false);

	return (
		<div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-sm">
			<span className="text-foreground">{cell.analyte}</span>
			<span className="text-xs text-muted-foreground">
				{ExamLabels.concordance(cell.concordance)}
			</span>

			<ModelMenu
				readings={cell.raw}
				disabled={arbitrate.isPending}
				onChoose={(model) => arbitrate.mutate({ id: cell.id, model })}
			/>

			<Button type="button" variant="secondary" size="sm" onClick={() => setEditing(true)}>
				{ExamLabels.editField.manual}
			</Button>

			{!!cell.editedBy && (
				<span className="text-xs text-muted-foreground">{ExamLabels.editedBy(cell.editedBy)}</span>
			)}

			{editing && <EditResultModal cell={cell} onClose={() => setEditing(false)} />}
		</div>
	);
}

export function Divergent({ cells }: { cells: ExamsPivot["divergent"] }) {
	if (cells.length === 0) {
		return null;
	}

	return (
		<div className="flex flex-col gap-2">
			<p className="text-xs font-medium uppercase tracking-wide text-destructive">
				Divergências entre leitores
			</p>

			{cells.map((cell) => (
				<DivergentRow key={cell.id} cell={cell} />
			))}
		</div>
	);
}
